import React, { useLayoutEffect } from 'react';
import classNames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { white } from '@fiverr-private/sass/helpers/index';
import { Fade, SlideFade, Duration } from '@fiverr-private/transition';
import { Portal } from '@fiverr-private/ui_utils';
import { useClickOutside, useKeyboardShortcuts } from '@fiverr-private/hooks';
import { PopoverContentProps } from '../types';
import { usePopoverContext } from '../PopoverContext';
import { Z_INDEX_MAP } from '../constants';
import classes from './index.module.scss';

const PopoverContent = ({
  children,
  mobileFullWidth = false,
  appendToBody = false,
  closeOnClickOutside = true,
  initialFocusRef,
  disablePadding = false,
  renderWhenClosed = false,
  disableBoxShadow = false,
  backgroundColor = white,
  zIndex = 'popover',
  animationType = 'fade',
  dataTestId,
}: PopoverContentProps) => {
  const {
    open,
    setOpen,
    triggerEvent,
    floatingData: { strategy, y, x, refs, isPositioned },
  } = usePopoverContext();
  const { isMobile } = getContext();

  const clickOutsideHandler = (e) => {
    const clickedOnAnchor = e.target === refs.reference.current;
    const clickedOnAnchorChild = (refs.reference.current as HTMLElement)?.contains(e.target as Node);
    if (clickedOnAnchor || clickedOnAnchorChild) {
      return;
    }
    setOpen(false);
  };

  useClickOutside(refs.floating, open && closeOnClickOutside ? clickOutsideHandler : undefined);
  useKeyboardShortcuts([
    {
      trigger: 'escape',
      handler: () => setOpen(false),
    },
  ]);

  useLayoutEffect(() => {
    if (isPositioned && refs.floating.current) {
      refs.floating.current.focus();
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      }
    }
  }, [isPositioned, refs.floating, initialFocusRef]);

  const contentProps = {
    tabIndex: open ? -1 : undefined,
    className: classNames(classes.content, {
      [classes.disablePadding]: disablePadding,
      'drop-shadow-z2': !disableBoxShadow,
    }),
    ref: refs.setFloating,
    'data-testid': dataTestId,
    style: {
      position: strategy,
      top: y || 0,
      left: x || 0,
      maxWidth: isMobile && mobileFullWidth ? undefined : 500,
      backgroundColor,
      zIndex: Z_INDEX_MAP[zIndex],
    },
    onMouseEnter:
      triggerEvent === 'hover'
        ? () => {
            setOpen(true);
          }
        : undefined,
    onMouseLeave:
      triggerEvent === 'hover'
        ? () => {
            setOpen(false);
          }
        : undefined,
  };

  const Animation = animationType === 'slideFade' ? SlideFade : Fade;
  const delay: Duration | undefined = triggerEvent === 'hover' ? 'fast3' : undefined;
  const animationProps = {
    delay,
    in: open,
    unmountOnExit: !renderWhenClosed,
  };

  if (appendToBody) {
    return (
      <Portal>
        <Animation {...animationProps}>
          <div {...contentProps}>{children}</div>
        </Animation>
      </Portal>
    );
  }
  return (
    <Animation {...animationProps}>
      <div {...contentProps}>{children}</div>
    </Animation>
  );
};

PopoverContent.displayName = 'PopoverContent';

export default PopoverContent;
